import { useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { PageTitle } from "../components/site/PageTitle";

const Field = ({ f, value, onChange }) => {
  const common = { className: "field", required: f.required, value, "data-testid": `req-field-${f.name}`, onChange: (e) => onChange(f.type === "checkbox" ? e.target.checked : e.target.value) };
  let input;
  if (f.type === "textarea") input = <textarea rows={5} {...common} placeholder={f.placeholder} />;
  else if (f.type === "select") input = <select {...common}>{f.options.map((o) => <option key={o}>{o}</option>)}</select>;
  else if (f.type === "checkbox")
    input = (
      <label className="flex items-center gap-3 text-sm text-slate-300">
        <input type="checkbox" checked={value} onChange={common.onChange} data-testid={common["data-testid"]} className="accent-[#00d436] w-4 h-4" /> {f.label}
      </label>
    );
  else input = <input type={f.type || "text"} min={f.min} max={f.max} {...common} placeholder={f.placeholder} />;
  return (
    <div className={f.wide ? "sm:col-span-2" : ""}>
      {f.type !== "checkbox" && <label className="block font-cond uppercase tracking-widest text-xs text-slate-400 mb-2">{f.label}{f.required && " *"}</label>}
      {input}
    </div>
  );
};

const RequestPage = ({ kind, kicker, title, intro, image, fields, success }) => {
  const initial = Object.fromEntries(fields.map((f) => [f.name, f.default ?? (f.type === "select" ? f.options[0] : "")]));
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...form };
      fields.filter((f) => f.type === "number").forEach((f) => (body[f.name] = Number(body[f.name])));
      await api.submitRequest(kind, body);
      setDone(true);
      setForm(initial);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageTitle kicker={kicker} title={title}>{intro}</PageTitle>
      <section className="px-5 lg:px-12 grid gap-10 lg:grid-cols-[1.3fr_1fr]">
        {done ? (
          <div className="card-surface rounded-2xl p-10 reveal" data-testid={`${kind}-success`}>
            <CheckCircle2 className="w-12 h-12 text-lime" />
            <h2 className="font-cond uppercase font-black text-4xl mt-5">Request sent</h2>
            <p className="text-slate-400 mt-3">{success}</p>
            <button onClick={() => setDone(false)} data-testid={`${kind}-send-another`} className="btn-line rounded-full px-6 py-3 mt-8 font-cond uppercase tracking-widest">Send another</button>
          </div>
        ) : (
          <form onSubmit={submit} className="card-surface rounded-2xl p-6 sm:p-10 grid sm:grid-cols-2 gap-6 reveal" data-testid={`${kind}-form`}>
            {fields.map((f) => <Field key={f.name} f={f} value={form[f.name]} onChange={(v) => setForm((s) => ({ ...s, [f.name]: v }))} />)}
            <button type="submit" disabled={busy} data-testid={`${kind}-submit-button`}
              className="sm:col-span-2 btn-lime rounded-full py-4 font-cond uppercase font-bold tracking-widest text-lg flex items-center justify-center gap-2">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Send request
            </button>
          </form>
        )}
        <div className="hidden lg:block rounded-2xl overflow-hidden min-h-[520px]">
          <img src={image} alt="" className="w-full h-full object-cover" />
        </div>
      </section>
    </>
  );
};

const U = (id) => `https://images.unsplash.com/${id}?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80`;
const todayISO = new Date().toISOString().slice(0, 10);

export const BookingPage = () => (
  <RequestPage kind="bookings" kicker="Bookings" title="Book 00shot"
    intro="Concerts, clubs, festivals and private events. Once our team confirms, the date shows as booked on the concert calendar."
    image={U("photo-1533174072545-7a4b6ad7a6c3")}
    success="Thanks! The team will review your booking and reach out by email. Confirmed dates appear on the concert calendar."
    fields={[
      { name: "name", label: "Your name", required: true },
      { name: "email", label: "Email", type: "email", required: true },
      { name: "phone", label: "Phone" },
      { name: "event_type", label: "Event type", type: "select", options: ["Concert", "Club night", "Festival", "Private event", "Other"] },
      { name: "event_name", label: "Event name", required: true },
      { name: "date", label: "Event date", type: "date", required: true, min: todayISO },
      { name: "venue", label: "Venue", required: true },
      { name: "city", label: "City", required: true },
      { name: "budget", label: "Budget / offer", placeholder: "$" },
      { name: "details", label: "Details", type: "textarea", wide: true, placeholder: "Set length, capacity, travel, anything else" },
    ]} />
);

export const FeaturePage = () => (
  <RequestPage kind="features" kicker="Collabs" title="Feature request"
    intro="Want a verse or hook from 00shot on your track? Send the details and a demo link."
    image={U("photo-1589903308904-1010c2294adc")}
    success="Thanks! The team will listen to your demo and get back to you by email."
    fields={[
      { name: "artist_name", label: "Artist name", required: true },
      { name: "email", label: "Email", type: "email", required: true },
      { name: "track_title", label: "Track title", required: true },
      { name: "genre", label: "Genre" },
      { name: "demo_link", label: "Demo link", type: "url", wide: true, placeholder: "SoundCloud, Dropbox, Google Drive…" },
      { name: "budget", label: "Budget", placeholder: "$" },
      { name: "timeline", label: "Timeline", placeholder: "e.g. needed by end of month" },
      { name: "message", label: "Message", type: "textarea", wide: true },
    ]} />
);

export const StudioBookingPage = () => (
  <RequestPage kind="studio" kicker="Recording studio" title="Book a studio session"
    intro="Request time in the 00shot studio. Pick a date and time — we'll confirm by email."
    image={U("photo-1618609377864-68609b857e90")}
    success="Thanks! We'll check the studio calendar and confirm your session by email."
    fields={[
      { name: "name", label: "Your name", required: true },
      { name: "email", label: "Email", type: "email", required: true },
      { name: "phone", label: "Phone" },
      { name: "session_type", label: "Session type", type: "select", options: ["Recording", "Mixing", "Mastering", "Writing session", "Podcast"] },
      { name: "date", label: "Date", type: "date", required: true, min: todayISO },
      { name: "start_time", label: "Start time", type: "time", required: true },
      { name: "hours", label: "Hours (1–12)", type: "number", required: true, min: 1, max: 12, default: 2 },
      { name: "engineer", label: "I need a sound engineer", type: "checkbox", default: true },
      { name: "notes", label: "Notes", type: "textarea", wide: true },
    ]} />
);
